import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  DirectionsCar as CarIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  LocalShipping as PartsIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface RepairJob {
  id: string;
  vehicleInfo: {
    make: string;
    model: string;
    year: number;
    vin: string;
    licensePlate: string;
  };
  customerInfo: {
    name: string;
    phone: string;
    email: string;
  };
  jobDetails: {
    title: string;
    description: string;
    type: 'maintenance' | 'repair' | 'diagnostic' | 'bodywork';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    status: 'pending' | 'in-progress' | 'waiting-parts' | 'completed' | 'cancelled';
    estimatedHours: number;
    actualHours: number;
    startDate: Date | null;
    completionDate: Date | null;
    assignedTechnician: string | null;
    assignedBay: string | null;
  };
  partsRequired: {
    id: string;
    name: string;
    quantity: number;
    status: 'in-stock' | 'ordered' | 'pending' | 'received';
    estimatedArrival: Date | null;
  }[];
  financials: {
    laborCost: number;
    partsCost: number;
    additionalCosts: number;
    discount: number;
    total: number;
    paid: boolean;
  };
  notes: string;
  createdAt: Date;
  updatedAt: Date;
}

export const RepairJobsBoard: React.FC = () => {
  const [jobs, setJobs] = useState<RepairJob[]>([
    {
      id: '1',
      vehicleInfo: {
        make: 'Toyota',
        model: 'Corolla',
        year: 2019,
        vin: 'JT2BF22K1W0123456',
        licensePlate: 'ABC123',
      },
      customerInfo: {
        name: 'John Smith',
        phone: '(123) 456-7890',
        email: 'john.smith@example.com',
      },
      jobDetails: {
        title: 'Brake Replacement',
        description: 'Replace front and rear brake pads and rotors',
        type: 'repair',
        priority: 'medium',
        status: 'in-progress',
        estimatedHours: 3,
        actualHours: 2.5,
        startDate: new Date(2023, 2, 15, 9, 0),
        completionDate: null,
        assignedTechnician: '1',
        assignedBay: '2',
      },
      partsRequired: [
        {
          id: 'p1',
          name: 'Front Brake Pads',
          quantity: 1,
          status: 'in-stock',
          estimatedArrival: null,
        },
        {
          id: 'p2',
          name: 'Rear Brake Pads',
          quantity: 1,
          status: 'in-stock',
          estimatedArrival: null,
        },
        {
          id: 'p3',
          name: 'Front Rotors',
          quantity: 2,
          status: 'in-stock',
          estimatedArrival: null,
        },
        {
          id: 'p4',
          name: 'Rear Rotors',
          quantity: 2,
          status: 'ordered',
          estimatedArrival: new Date(2023, 2, 16),
        },
      ],
      financials: {
        laborCost: 250,
        partsCost: 320,
        additionalCosts: 30,
        discount: 50,
        total: 550,
        paid: false,
      },
      notes: 'Customer requested OEM parts only',
      createdAt: new Date(2023, 2, 14, 15, 30),
      updatedAt: new Date(2023, 2, 15, 10, 45),
    },
    {
      id: '2',
      vehicleInfo: {
        make: 'Honda',
        model: 'Civic',
        year: 2020,
        vin: '1HGCM82633A123456',
        licensePlate: 'XYZ789',
      },
      customerInfo: {
        name: 'Sarah Johnson',
        phone: '(234) 567-8901',
        email: 'sarah.j@example.com',
      },
      jobDetails: {
        title: 'Oil Change and Inspection',
        description: 'Regular maintenance - oil change, filter replacement, and general inspection',
        type: 'maintenance',
        priority: 'low',
        status: 'completed',
        estimatedHours: 1,
        actualHours: 1.2,
        startDate: new Date(2023, 2, 15, 11, 0),
        completionDate: new Date(2023, 2, 15, 12, 15),
        assignedTechnician: '2',
        assignedBay: '1',
      },
      partsRequired: [
        {
          id: 'p5',
          name: 'Oil Filter',
          quantity: 1,
          status: 'in-stock',
          estimatedArrival: null,
        },
        {
          id: 'p6',
          name: 'Synthetic Oil (5W-30)',
          quantity: 5,
          status: 'in-stock',
          estimatedArrival: null,
        },
      ],
      financials: {
        laborCost: 80,
        partsCost: 65,
        additionalCosts: 0,
        discount: 0,
        total: 145,
        paid: true,
      },
      notes: 'Customer waiting in lounge',
      createdAt: new Date(2023, 2, 14, 9, 15),
      updatedAt: new Date(2023, 2, 15, 12, 20),
    },
    {
      id: '3',
      vehicleInfo: {
        make: 'BMW',
        model: 'X5',
        year: 2021,
        vin: 'WBAKJ4C51BC123456',
        licensePlate: 'LMN456',
      },
      customerInfo: {
        name: 'Michael Brown',
        phone: '(345) 678-9012',
        email: 'm.brown@example.com',
      },
      jobDetails: {
        title: 'Engine Diagnostic',
        description: 'Check engine light on, perform diagnostic and repair',
        type: 'diagnostic',
        priority: 'high',
        status: 'waiting-parts',
        estimatedHours: 2,
        actualHours: 1.5,
        startDate: new Date(2023, 2, 14, 14, 0),
        completionDate: null,
        assignedTechnician: '3',
        assignedBay: '3',
      },
      partsRequired: [
        {
          id: 'p7',
          name: 'Oxygen Sensor',
          quantity: 1,
          status: 'ordered',
          estimatedArrival: new Date(2023, 2, 17),
        },
        {
          id: 'p8',
          name: 'Gasket Set',
          quantity: 1,
          status: 'pending',
          estimatedArrival: null,
        },
      ],
      financials: {
        laborCost: 180,
        partsCost: 250,
        additionalCosts: 0,
        discount: 0,
        total: 430,
        paid: false,
      },
      notes: 'Customer approved up to $600 for repairs',
      createdAt: new Date(2023, 2, 13, 16, 45),
      updatedAt: new Date(2023, 2, 14, 15, 30),
    },
  ]);

  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [openJobDialog, setOpenJobDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState<RepairJob | null>(null);

  const handleOpenJobDialog = (job: RepairJob | null) => {
    setSelectedJob(job);
    setOpenJobDialog(true);
  };

  const handleCloseJobDialog = () => {
    setOpenJobDialog(false);
    setSelectedJob(null);
  };

  const handleSaveJob = () => {
    // Implementation for saving job changes
    handleCloseJobDialog();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in-progress':
        return 'primary';
      case 'waiting-parts':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'in-stock':
        return 'success';
      case 'ordered':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'received':
        return 'success';
      case 'low':
        return 'success';
      case 'medium':
        return 'info';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'N/A';
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filterJobs = () => {
    let filteredJobs = [...jobs];

    // Filter by tab
    if (currentTab === 1) {
      filteredJobs = filteredJobs.filter(job => job.jobDetails.status === 'pending');
    } else if (currentTab === 2) {
      filteredJobs = filteredJobs.filter(job => job.jobDetails.status === 'in-progress');
    } else if (currentTab === 3) {
      filteredJobs = filteredJobs.filter(job => job.jobDetails.status === 'waiting-parts');
    } else if (currentTab === 4) {
      filteredJobs = filteredJobs.filter(job => job.jobDetails.status === 'completed');
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredJobs = filteredJobs.filter(
        job =>
          job.vehicleInfo.make.toLowerCase().includes(query) ||
          job.vehicleInfo.model.toLowerCase().includes(query) ||
          job.vehicleInfo.licensePlate.toLowerCase().includes(query) ||
          job.customerInfo.name.toLowerCase().includes(query) ||
          job.jobDetails.title.toLowerCase().includes(query)
      );
    }

    return filteredJobs;
  };

  const filteredJobs = filterJobs();

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Repair Jobs Board
        </Typography>

        {/* Search and Filter */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by vehicle, customer, or job title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenJobDialog(null)}
            >
              New Repair Job
            </Button>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Jobs" />
            <Tab label="Pending" />
            <Tab label="In Progress" />
            <Tab label="Waiting Parts" />
            <Tab label="Completed" />
          </Tabs>
        </Box>

        {/* Jobs Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredJobs.map((job) => (
                <TableRow key={job.id}>
                  <TableCell>#{job.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CarIcon sx={{ mr: 1 }} />
                      <Box>
                        <Typography variant="body2">
                          {job.vehicleInfo.make} {job.vehicleInfo.model} ({job.vehicleInfo.year})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.vehicleInfo.licensePlate}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{job.customerInfo.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.customerInfo.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>{job.jobDetails.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        job.jobDetails.type.charAt(0).toUpperCase() + job.jobDetails.type.slice(1)
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        job.jobDetails.priority.charAt(0).toUpperCase() +
                        job.jobDetails.priority.slice(1)
                      }
                      color={getStatusColor(job.jobDetails.priority) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        job.jobDetails.status
                          .split('-')
                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                          .join(' ')
                      }
                      color={getStatusColor(job.jobDetails.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{job.jobDetails.assignedTechnician || 'Unassigned'}</TableCell>
                  <TableCell>{formatDate(job.jobDetails.startDate)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpenJobDialog(job)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Job Details Dialog */}
        <Dialog open={openJobDialog} onClose={handleCloseJobDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedJob ? 'Edit Repair Job' : 'Create New Repair Job'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Tabs value={0} sx={{ mb: 2 }}>
                <Tab label="Job Details" />
                <Tab label="Vehicle Info" />
                <Tab label="Customer Info" />
                <Tab label="Parts" />
                <Tab label="Financials" />
              </Tabs>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    defaultValue={selectedJob?.jobDetails.title || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Job Type</InputLabel>
                    <Select
                      label="Job Type"
                      defaultValue={selectedJob?.jobDetails.type || 'repair'}
                    >
                      <MenuItem value="maintenance">Maintenance</MenuItem>
                      <MenuItem value="repair">Repair</MenuItem>
                      <MenuItem value="diagnostic">Diagnostic</MenuItem>
                      <MenuItem value="bodywork">Bodywork</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      label="Priority"
                      defaultValue={selectedJob?.jobDetails.priority || 'medium'}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="urgent">Urgent</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      defaultValue={selectedJob?.jobDetails.status || 'pending'}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in-progress">In Progress</MenuItem>
                      <MenuItem value="waiting-parts">Waiting for Parts</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Estimated Hours"
                    type="number"
                    defaultValue={selectedJob?.jobDetails.estimatedHours || 1}
                    InputProps={{
                      inputProps: { min: 0, step: 0.5 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Actual Hours"
                    type="number"
                    defaultValue={selectedJob?.jobDetails.actualHours || 0}
                    InputProps={{
                      inputProps: { min: 0, step: 0.5 },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Job Description"
                    multiline
                    rows={4}
                    defaultValue={selectedJob?.jobDetails.description || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    defaultValue={selectedJob?.notes || ''}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseJobDialog}>Cancel</Button>
            <Button onClick={handleSaveJob} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 